import React, { useCallback, useEffect, useMemo, useState } from "react";
import Col from "react-bootstrap/Col";
import Button from "react-bootstrap/Button";
import Form from "react-bootstrap/Form";
import Modal from "react-bootstrap/Modal";
import OverlayTrigger from "react-bootstrap/OverlayTrigger";
import Row from "react-bootstrap/Row";
import Tooltip from "react-bootstrap/Tooltip";

import {
  listMessages,
  markConversationRead,
} from "../../whatsappApi";
import Composer from "./Composer";
import ContactList from "./ContactList";
import MessagePane from "./MessagePane";
import ThreadList from "./ThreadList";
import useWhatsAppSocket from "./useWhatsAppSocket";
import "../Accounts/CreateInvoice.css";
import "./whatsapp.css";

/**
 * InboxModal: the two-pane inbox.
 *
 * Why a modal rather than a dashboard pane: the dashboard gives WhatsApp one
 * column, and a thread list plus a message pane cannot share it at any useful
 * size. The dashboard hosts the thread list; the message pane opens here.
 *
 * Shape: a standard near-full-width modal (`modal-85w`, as Receipt and Credit
 * Note use) rather than a fullscreen one. It used to be `fullscreen="lg-down"`,
 * which made the inbox behave unlike every other modal in the app: edge to
 * edge with no backdrop on the sizes most people open it at.
 *
 * The left pane has two jobs and one header. The search box and the New
 * message button never move; what changes underneath them is whether you are
 * searching your conversations or searching the people you could start one
 * with. Starting a conversation used to be a modal stacked on this modal,
 * which made the same task feel like a different screen.
 *
 * Conventions: /CLAUDE.md §1.8 (header tint, h6 title, subject suffix). No
 * Modal.Footer, because the composer is the action surface and a footer takes
 * vertical space the message pane needs.
 */
export default function InboxModal({
  show,
  onHide,
  practiceNumber,
  practiceName,
  conversations,
  templates,
  loadingThreads,
  initialConversationId,
  onConversationsChanged,
  onViewPatient,
  showToast,
}) {
  const [activeId, setActiveId] = useState(initialConversationId || null);
  const [messages, setMessages] = useState([]);
  const [loadingMessages, setLoadingMessages] = useState(false);
  const [filter, setFilter] = useState("");
  const [search, setSearch] = useState("");
  const [contactQuery, setContactQuery] = useState("");
  // What the left pane is doing: listing threads, or picking someone new.
  const [paneMode, setPaneMode] = useState("threads");
  // Mobile shows one pane at a time; desktop shows both.
  const [mobilePane, setMobilePane] = useState("threads");

  useEffect(() => {
    if (!show) return;
    setActiveId(initialConversationId || null);
    setPaneMode("threads");
    setContactQuery("");
  }, [show, initialConversationId]);

  const active = useMemo(
    () => conversations.find((c) => String(c._id) === String(activeId)) || null,
    [conversations, activeId],
  );

  const loadMessages = useCallback(
    async (conversationId) => {
      if (!conversationId) return;
      setLoadingMessages(true);
      try {
        const data = await listMessages(practiceNumber, conversationId);
        setMessages(data.messages || []);
      } catch (err) {
        showToast?.(
          "danger",
          err?.response?.data?.error || "Could not load this conversation.",
        );
      } finally {
        setLoadingMessages(false);
      }
    },
    [practiceNumber, showToast],
  );

  useEffect(() => {
    if (!show || !activeId) return;
    loadMessages(activeId);
  }, [show, activeId, loadMessages]);

  const select = async (conversation) => {
    setActiveId(conversation._id);
    setMobilePane("messages");
    if (conversation.unreadCount > 0) {
      try {
        await markConversationRead(practiceNumber, conversation._id);
        onConversationsChanged?.();
      } catch (_err) {
        // A failed read-receipt is cosmetic; the thread still opens.
      }
    }
  };

  const filtered = useMemo(() => {
    let list = conversations;
    if (filter === "unread") list = list.filter((c) => c.unreadCount > 0);
    if (filter === "unknown")
      list = list.filter((c) => !c.patientId && !c.accountId);
    if (search.trim()) {
      const needle = search.trim().toLowerCase();
      list = list.filter((c) =>
        [c.patientName, c.accountName, c.profileName, c.displayPhone, c.waId]
          .filter(Boolean)
          .some((v) => String(v).toLowerCase().includes(needle)),
      );
    }
    return list;
  }, [conversations, filter, search]);

  // Append without duplicating: the same message can arrive twice, once as the
  // send call's return value and once over the socket, and a thread that shows
  // every outbound line twice is worse than one that shows it late.
  const upsertMessage = useCallback((message) => {
    if (!message?._id) return;
    setMessages((prev) => {
      const id = String(message._id);
      const at = prev.findIndex((m) => String(m._id) === id);
      if (at === -1) return [...prev, message];
      const next = prev.slice();
      next[at] = { ...next[at], ...message };
      return next;
    });
  }, []);

  /* ---- optimistic outbound -------------------------------------------- */

  /** The bubble that appears the instant Send is pressed. */
  const addPending = useCallback((pending) => {
    setMessages((prev) => [...prev, pending]);
  }, []);

  /** Swap the pending bubble for the row the server actually stored. */
  const resolvePending = useCallback((pendingId, message) => {
    setMessages((prev) => {
      const without = prev.filter((m) => String(m._id) !== String(pendingId));
      if (!message?._id) return without;
      const id = String(message._id);
      const at = without.findIndex((m) => String(m._id) === id);
      if (at === -1) return [...without, message];
      const next = without.slice();
      next[at] = { ...next[at], ...message };
      return next;
    });
  }, []);

  /**
   * Turn the pending bubble into a failed one.
   *
   * When Meta rejected the send the server wrote a row and handed it back, so
   * that row replaces the bubble and carries the retry affordance. When the
   * server refused before calling Meta there is no row, and the bubble is
   * marked failed in place with the reason. Otherwise the user gets a toast
   * and an unchanged thread, which reads as the button having done nothing.
   */
  const failPending = useCallback(
    (pendingId, reason, storedMessage) => {
      if (storedMessage?._id) {
        resolvePending(pendingId, storedMessage);
        return;
      }
      setMessages((prev) =>
        prev.map((m) =>
          String(m._id) === String(pendingId)
            ? { ...m, status: "failed", errorMessage: reason }
            : m,
        ),
      );
    },
    [resolvePending],
  );

  const onSent = useCallback(
    (pendingId, message) => {
      resolvePending(pendingId, message);
      onConversationsChanged?.();
    },
    [resolvePending, onConversationsChanged],
  );

  /**
   * Live updates for the thread that is open.
   *
   * InboxSection already listens for these, but only to refresh the thread
   * LIST. Nothing was feeding the message pane, so anything that happened
   * while the modal was open (an inbound reply, a delivery receipt, and the
   * send that started it) only appeared after closing and reopening, which
   * remounts the modal and refetches.
   */
  useWhatsAppSocket({
    enabled: show && !!practiceNumber,
    handlers: {
      "whatsapp:message": (payload) => {
        if (String(payload.practiceNumber) !== String(practiceNumber)) return;
        if (String(payload.conversationId) !== String(activeId)) return;
        upsertMessage(payload.message);
      },
      "whatsapp:status": (payload) => {
        if (String(payload.practiceNumber) !== String(practiceNumber)) return;
        if (String(payload.conversationId) !== String(activeId)) return;
        // Keyed by waMessageId, because a status report is all Meta gives us.
        setMessages((prev) =>
          prev.map((m) =>
            m.waMessageId && m.waMessageId === payload.waMessageId
              ? { ...m, status: payload.status, errorMessage: payload.errorMessage }
              : m,
          ),
        );
      },
      "whatsapp:message-updated": (payload) => {
        if (String(payload.practiceNumber) !== String(practiceNumber)) return;
        if (String(payload.conversationId) !== String(activeId)) return;
        // Media finished downloading, so refetch for the bubble's blob id.
        loadMessages(activeId);
      },
    },
  });

  /* ---- left-pane mode -------------------------------------------------- */

  const openContacts = () => {
    setPaneMode("contacts");
    setContactQuery("");
    // Clear the right pane: the thread you were reading is not the one you are
    // about to start, and leaving it up makes the composer below it ambiguous.
    setActiveId(null);
    setMessages([]);
    setMobilePane("threads");
  };

  const closeContacts = () => {
    setPaneMode("threads");
    setContactQuery("");
  };

  const onConversationStarted = (conversation) => {
    closeContacts();
    // Refresh the list so the new thread is in `conversations` before we select
    // it. `active` is derived from that array, and selecting an id the list
    // does not hold yet would render an empty pane.
    onConversationsChanged?.();
    setActiveId(conversation._id);
    setMessages([]);
    setMobilePane("messages");
  };

  const contactsMode = paneMode === "contacts";

  return (
    <Modal show={show} onHide={onHide} dialogClassName="modal-85w">
      <Modal.Header
        closeButton
        style={{ background: "#CFE2FF" }}
        className="rounded-0 text-primary d-flex align-items-center"
      >
        <Modal.Title as="h6" className="m-0">
          WhatsApp{practiceName ? `: ${practiceName}` : ""}
        </Modal.Title>
      </Modal.Header>

      <Modal.Body className="p-0 wa-inbox-body">
        <Row className="g-0 h-100">
          <Col
            md={4}
            className={`wa-thread-col ${
              mobilePane === "messages" ? "d-none d-md-block" : ""
            }`}
          >
            {/* One header, two jobs. It stays put; the list below it swaps. */}
            <div className="wa-pane-header p-2 border-bottom d-flex gap-2 align-items-center">
              <Form.Control
                type="search"
                size="sm"
                placeholder={
                  contactsMode
                    ? "Search patients and account holders"
                    : "Search name or number"
                }
                value={contactsMode ? contactQuery : search}
                onChange={(e) =>
                  contactsMode
                    ? setContactQuery(e.target.value)
                    : setSearch(e.target.value)
                }
              />

              {contactsMode ? (
                <OverlayTrigger
                  placement="top"
                  overlay={<Tooltip>Back to conversations</Tooltip>}
                >
                  <Button
                    variant="outline-secondary"
                    size="sm"
                    onClick={closeContacts}
                    aria-label="Back to conversations"
                  >
                    <i className="fa-solid fa-xmark" />
                  </Button>
                </OverlayTrigger>
              ) : (
                <>
                  <Form.Select
                    size="sm"
                    style={{ maxWidth: 110 }}
                    value={filter}
                    onChange={(e) => setFilter(e.target.value)}
                    aria-label="Filter conversations"
                  >
                    <option value="">All</option>
                    <option value="unread">Unread</option>
                    <option value="unknown">Unknown</option>
                  </Form.Select>

                  <OverlayTrigger
                    placement="top"
                    overlay={<Tooltip>New message</Tooltip>}
                  >
                    <Button
                      variant="primary"
                      size="sm"
                      onClick={openContacts}
                      aria-label="New message"
                    >
                      <i className="fa-solid fa-pen-to-square" />
                    </Button>
                  </OverlayTrigger>
                </>
              )}
            </div>

            {contactsMode ? (
              <ContactList
                practiceNumber={practiceNumber}
                query={contactQuery}
                onStarted={onConversationStarted}
                showToast={showToast}
              />
            ) : (
              <ThreadList
                conversations={filtered}
                activeId={activeId}
                onSelect={select}
                loading={loadingThreads}
              />
            )}
          </Col>

          <Col
            md={8}
            className={`wa-message-col ${
              mobilePane === "threads" ? "d-none d-md-flex" : "d-flex"
            }`}
          >
            <MessagePane
              practiceNumber={practiceNumber}
              conversation={active}
              messages={messages}
              loading={loadingMessages}
              onBack={() => setMobilePane("threads")}
              onViewPatient={onViewPatient}
            />
            <Composer
              practiceNumber={practiceNumber}
              practiceName={practiceName}
              conversation={active}
              templates={templates}
              onPending={addPending}
              onSent={onSent}
              onFailed={failPending}
              onConsentChanged={onConversationsChanged}
              showToast={showToast}
            />
          </Col>
        </Row>
      </Modal.Body>
    </Modal>
  );
}
